"use client";

import { useState } from "react";
import Papa from "papaparse";
import { Upload, FileDown, AlertCircle, X } from "lucide-react";
import { toast } from "react-toastify";
import {LocalStorageKeys} from "../dictionaries/localstorage";

// ~50kb per request for typical client rows, well under the default 100kb body limit
const BATCH_SIZE = 500;

interface BulkCsvUploadProps {
    title: string;
    endpoint: string;
    csvHeaders: string[];
    sampleRow: Record<string, any>;
    onClose: () => void;
    onSuccess?: () => void;
}

export default function BulkCsvUpload({
                                          title,
                                          endpoint,
                                          csvHeaders,
                                          sampleRow,
                                          onClose,
                                          onSuccess
                                      }: BulkCsvUploadProps) {
    const [rows, setRows] = useState<any[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    // Per-row outcome of the last upload, shown when anything was skipped or failed
    const [report, setReport] = useState<{
        added: number;
        skipped: { row: any; reason: string }[];
        failed: { row: any; reason: string }[];
    } | null>(null);

    const validateRows = (data: any[]) => {
        const validationErrors: string[] = [];

        data.forEach((row, index) => {
            csvHeaders.forEach((header) => {
                if (!(header in row)) {
                    validationErrors.push(
                        `Row ${index + 1}: Missing column "${header}"`
                    );
                }
            });
        });

        setErrors(validationErrors);
    };

    const handleFile = (file: File) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (result) => {
                const parsed = result.data as any[];
                setRows(parsed);
                validateRows(parsed);
            },
        });
    };

    const uploadCsv = async () => {
        if (errors.length > 0) return;

        setUploading(true);
        setProgress(0);
        setReport(null);

        try {
            const token = localStorage.getItem(LocalStorageKeys.AUTH_TOKEN);

            let successCount = 0;
            const skippedRows: { row: any; reason: string }[] = [];
            const failedRows: { row: any; reason: string }[] = [];

            // Send in batches so large files stay under the server's body size limit
            for (let i = 0; i < rows.length; i += BATCH_SIZE) {
                const batch = rows.slice(i, i + BATCH_SIZE);

                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        [title.toLowerCase()]: batch,
                    }),
                });

                if (res.status !== 200 && res.status !== 201) {
                    const errorData = await res.json().catch(() => null);

                    toast.error(
                        `${errorData?.error || errorData?.message || `Upload failed (status ${res.status})`}` +
                        (successCount ? ` — ${successCount} ${title} uploaded before the error` : "")
                    );

                    setUploading(false);
                    if (successCount) onSuccess?.();
                    return;
                }

                const data = await res.json();

                successCount += data.success?.length || 0;
                (data.skipped || []).forEach((s: any) => skippedRows.push({ row: s.client, reason: s.reason }));
                (data.failed || []).forEach((f: any) => failedRows.push({ row: f.client, reason: f.error }));
                setProgress(Math.min(i + BATCH_SIZE, rows.length));
            }

            setRows([]);
            onSuccess?.();

            if (skippedRows.length === 0 && failedRows.length === 0) {
                toast.success(`✅ Successfully uploaded ${successCount} ${title}`);
                onClose();
            } else {
                // Keep the modal open so the merchant can see who wasn't added and why
                setReport({ added: successCount, skipped: skippedRows, failed: failedRows });
            }

        } catch (err) {
            toast.error("Network error while uploading CSV");
        }

        setUploading(false);
    };

    const downloadReport = () => {
        if (!report) return;
        const lines = [
            ...report.failed.map(({ row, reason }) => ({ ...row, status: "Not added", reason })),
            ...report.skipped.map(({ row, reason }) => ({ ...row, status: "Skipped", reason })),
        ];
        const blob = new Blob([Papa.unparse(lines)], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${title}-upload-report.csv`;
        link.click();
    };

    const downloadSample = () => {
        const csv = Papa.unparse([sampleRow]);
        const blob = new Blob([csv], { type: "text/csv" });

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${title}-sample.csv`;
        link.click();
    };

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

            <div className="bg-white rounded-xl shadow-xl w-[520px] p-6 relative space-y-4">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                    <X size={18} />
                </button>

                <h2 className="text-lg font-semibold">{title} CSV Upload</h2>

                <div className="flex gap-3">
                    <button
                        onClick={downloadSample}
                        className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50"
                    >
                        <FileDown size={16} />
                        Sample CSV
                    </button>

                    <label className="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm cursor-pointer hover:bg-gray-50">
                        <Upload size={16} />
                        Upload CSV
                        <input
                            type="file"
                            accept=".csv"
                            hidden
                            onChange={(e) => {
                                if (e.target.files?.[0]) handleFile(e.target.files[0]);
                            }}
                        />
                    </label>
                </div>

                {errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-sm">
                        <div className="flex items-center gap-2 text-red-600">
                            <AlertCircle size={16} />
                            CSV Validation Errors
                        </div>

                        <ul className="list-disc ml-6 mt-2">
                            {errors.map((e, i) => (
                                <li key={i}>{e}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {rows.length > 0 && (
                    <div className="text-sm text-gray-600">
                        {rows.length} rows ready to upload
                    </div>
                )}

                {report && (
                    <div className="border rounded-lg p-3 text-sm space-y-2">
                        <div className="font-medium">Upload finished</div>
                        <ul className="space-y-0.5">
                            <li className="text-green-700">{report.added} added</li>
                            {report.skipped.length > 0 && (
                                <li className="text-gray-600">{report.skipped.length} skipped (already in the system)</li>
                            )}
                            {report.failed.length > 0 && (
                                <li className="text-red-600">{report.failed.length} not added</li>
                            )}
                        </ul>
                        {report.failed.length > 0 && (
                            <ul className="max-h-40 overflow-y-auto list-disc ml-5 text-xs text-gray-700">
                                {report.failed.map(({ row, reason }, i) => (
                                    <li key={i}>
                                        {row?.name || row?.title || `Row ${i + 1}`}: {reason}
                                    </li>
                                ))}
                            </ul>
                        )}
                        <button
                            onClick={downloadReport}
                            className="flex items-center gap-2 px-3 py-1.5 border rounded-lg text-xs hover:bg-gray-50"
                        >
                            <FileDown size={14} />
                            Download report
                        </button>
                    </div>
                )}

                <button
                    disabled={uploading || rows.length === 0 || errors.length > 0}
                    onClick={uploadCsv}
                    className="bg-black text-white px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                >
                    {uploading ? `Uploading... ${progress}/${rows.length}` : "Upload"}
                </button>
            </div>
        </div>
    );
}